/// <reference types="google.maps" />
import { environment } from '@environments/environments';

const API_URL = 'https://maps.googleapis.com/maps/api/js';
const API_VERSION = 'weekly';
const CALLBACK_NAME = '__ib__';
const DEFAULT_LIBRARIES = ['places'];

/**
 * Installs the Google Maps dynamic library import bootstrap, reading the API key
 * from the active environment file so every configuration (local, dev, prod)
 * uses its own key.
 *
 * The API script is only fetched the first time `google.maps.importLibrary` is
 * called, so app startup never blocks on maps.googleapis.com.
 */
export const loadGoogleMapsApi = (): void => {
    const google = (window.google ??= {} as typeof window.google);
    const maps = (google.maps ??= {} as typeof google.maps);

    // Already bootstrapped - loading the API twice makes Google log a warning.
    if (maps.importLibrary) return;

    const requestedLibraries = new Set<string>();
    let apiLoaded: Promise<void> | undefined;

    const loadApi = (): Promise<void> => (apiLoaded ??= new Promise<void>((resolve, reject) => {
        const params = new URLSearchParams({
            v: API_VERSION,
            libraries: [...requestedLibraries, ...DEFAULT_LIBRARIES].join(','),
            key: environment.mapKey,
            callback: `google.maps.${CALLBACK_NAME}`,
        });

        const script = document.createElement('script');
        script.src = `${API_URL}?${params}`;
        script.nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce ?? '';
        script.onerror = () => reject(new Error('The Google Maps JavaScript API could not load.'));

        maps[CALLBACK_NAME] = resolve;
        document.head.append(script);
    }));

    // The shim stands in until the API script loads and replaces it with the real
    // implementation, which the delegating call below then picks up.
    maps.importLibrary = ((name: string) => {
        requestedLibraries.add(name);
        return loadApi().then(() => maps.importLibrary(name));
    }) satisfies typeof maps.importLibrary;
};
